import React from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import FieldLabel from '@/v2/components/common/FieldLabel'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsLabels,
  useAddLabel,
  useUpdateLabel,
  useDeleteLabel,
} from '@/v2/hooks/settings/useSettingsLabels'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import {
  SettingsPage,
  SettingsEmptyState,
  PrimaryButton,
  CardGrid,
  Card,
  CardTitle,
  ColorGrid,
  ColorOption,
} from '@/v2/components/settings/shared'

const ColorIndicator = styled.div`
  width: 100%;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: ${props => props.color};
`

const TutorialSection = styled.div`
  margin-top: 2rem;
`

const TutorialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 1.5rem;
`

const TutorialStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const StepIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #6b7280;
`

const StepTitle = styled.h6`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const StepDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
`

const LABEL_COLORS = [
  { name: 'ivory', hex: '#bbad8f' },
  { name: 'blush', hex: '#ca8f88' },
  { name: 'cherry', hex: '#d15252' },
  { name: 'peach', hex: '#f1864b' },
  { name: 'mustard', hex: '#dcb52b' },
  { name: 'lemon', hex: '#799a4f' },
  { name: 'turquoise', hex: '#66adb6' },
  { name: 'water', hex: '#5ea3e7' },
  { name: 'lavender', hex: '#6a7bd8' },
  { name: 'violet', hex: '#9779cc' },
  { name: 'rose', hex: '#dc92c3' },
  { name: 'indigo', hex: '#77819c' },
]

const colorToHex = name => {
  const found = LABEL_COLORS.find(c => c.name === name)
  return found ? found.hex : name
}

const Labels = () => {
  const { labels, isLoading } = useSettingsLabels()
  const { addLabel, isPending: isAdding } = useAddLabel()
  const { updateLabel, isPending: isUpdating } = useUpdateLabel()
  const { deleteLabel, isPending: isDeleting } = useDeleteLabel()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForCreate,
    openForEdit,
    close,
    updateField,
  } = useFormModal({ name: '', color: 'water' })

  const isSaving = isAdding || isUpdating

  const handleEdit = label => {
    openForEdit(label, l => ({ name: l.name, color: l.color }))
  }

  const handleDelete = async labelId => {
    if (confirm('Are you sure you want to delete this label?')) {
      try {
        await deleteLabel(labelId)
        onOpenSnackbar('Label deleted successfully', 'success')
        close()
      } catch (error) {
        onOpenSnackbar('Failed to delete label', 'error')
      }
    }
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return
    }

    const labelPayload = { name: formData.name, color: formData.color }

    try {
      if (isEditing) {
        await updateLabel({ id: editingItem.id, ...labelPayload })
        onOpenSnackbar('Label updated successfully', 'success')
      } else {
        await addLabel(labelPayload)
        onOpenSnackbar('Label created successfully', 'success')
      }
      close()
    } catch (error) {
      onOpenSnackbar('Failed to save label', 'error')
    }
  }

  return (
    <SettingsPage
      title="Labels"
      subtitle="Create and manage labels for categorizing appointments"
      loading={isLoading}
      actions={
        <PrimaryButton onClick={openForCreate}>+ Add Label</PrimaryButton>
      }
    >
      {labels.length === 0 ? (
        <>
          <SettingsEmptyState
            icon={
              <svg
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z" />
              </svg>
            }
            heading="You haven't added any label yet"
            description={
              <>
                Click on <strong>Add label</strong> to create the first one
              </>
            }
            actionLabel="Add label"
            onAction={openForCreate}
          />

          <TutorialSection>
            <TutorialGrid>
              <TutorialStep>
                <StepIcon>
                  <svg
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </StepIcon>
                <StepTitle>1. How it works</StepTitle>
                <StepDescription>
                  You can create and use labels to improve your workflow.
                </StepDescription>
              </TutorialStep>

              <TutorialStep>
                <StepIcon>
                  <svg
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z" />
                  </svg>
                </StepIcon>
                <StepTitle>2. Creating labels</StepTitle>
                <StepDescription>
                  Name your labels with purpose. Common examples are:{' '}
                  <strong>Ready</strong>, <strong>No-show</strong> and{' '}
                  <strong>Allergic</strong>.
                </StepDescription>
              </TutorialStep>

              <TutorialStep>
                <StepIcon>
                  <svg
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                  </svg>
                </StepIcon>
                <StepTitle>3. Using labels</StepTitle>
                <StepDescription>
                  To tag an appointment, open it in the calendar and click{' '}
                  <strong>Add Label</strong>.
                </StepDescription>
              </TutorialStep>
            </TutorialGrid>
          </TutorialSection>
        </>
      ) : (
        <CardGrid $minWidth="200px">
          {labels.map(label => (
            <Card key={label.id} onClick={() => handleEdit(label)}>
              <CardTitle $mb="1rem">{label.name}</CardTitle>
              <ColorIndicator color={colorToHex(label.color)} />
            </Card>
          ))}
        </CardGrid>
      )}

      <Modal
        isOpen={showModal}
        title={isEditing ? 'Edit Label' : 'New Label'}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel={isEditing ? 'Save Changes' : 'Create Label'}
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={false}
        maxWidth="sm"
        fullWidth
        showExtraButton={isEditing}
        extraButtonLabel="Delete Label"
        extraButtonColor="error"
        onExtraButtonClick={() => handleDelete(editingItem?.id)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Label Name"
            placeholder="Enter label name (e.g., Ready, No-show, Urgent)"
            value={formData.name}
            onChange={val => updateField('name', val)}
          />
          <div>
            <FieldLabel>Color</FieldLabel>
            <ColorGrid>
              {LABEL_COLORS.map(c => (
                <ColorOption
                  key={c.name}
                  type="button"
                  color={c.hex}
                  $selected={formData.color === c.name}
                  onClick={() => updateField('color', c.name)}
                  title={c.name}
                />
              ))}
            </ColorGrid>
          </div>
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default Labels
